import { useState, type FormEvent } from 'react'
import {
  AleloButton,
  AleloError,
  AleloInput,
  AleloLayout,
  AleloPasswordInput,
} from '@/components/AleloLayout'
import { RangoRadioGroup } from '@/components/rango'
import { AleloCaptcha } from '@/components/AleloCaptcha'
import { AleloStepper } from '@/components/AleloStepper'
import styles from '@/components/AleloLayout.module.css'
import {
  EMAIL_MAX_LENGTH,
  NAME_LIMITS,
  normalizeEmail,
  normalizePersonName,
  sanitizeEmailInput,
  toNameCase,
  validateEmail,
  validateMotherName,
  hasMotherNameFor,
  validatePersonName,
} from '@/utils/authValidation'
import {
  birthDateToIso,
  formatBirthDateInput,
  validateBirthDate,
} from '@/utils/birthDate'
import { transformInput } from '@/utils/inputTransform'
import { formatCpf, isValidCpf } from '@/utils/cpf'
import { SignupOtpStep } from '@/screens/signup-shared/SignupOtpStep'
import { useSignupVerification } from '@/screens/signup-shared/useSignupVerification'
import { useSignupManager, type SignupFormPayload } from './hooks/useSignupManager'
import locales from './locales/pt-BR.json'
import { errorCopy } from '@/utils/transactionError'

type Values = {
  cpf: string
  givenName: string
  familyName: string
  email: string
  dateOfBirth: string
  gender: string
  motherName: string
  password: string
  confirm: string
}

type FieldKey = keyof Values

/** id do input de cada campo, na ordem visual: o foco vai para o primeiro com erro. */
const INPUT_IDS: Record<FieldKey, string> = {
  cpf: 'cpf',
  givenName: 'given_name',
  familyName: 'family_name',
  email: 'email',
  dateOfBirth: 'date_of_birth',
  gender: 'gender',
  motherName: 'mother_name',
  password: 'password',
  confirm: 'confirm',
}

const FIELD_KEYS = Object.keys(INPUT_IDS) as FieldKey[]

/** Nome como será gravado: normalizado e com a inicial de cada palavra maiúscula. */
const cleanName = (value: string) => toNameCase(normalizePersonName(value))

/** Normalização cosmética aplicada ao sair do campo e no envio (RN-005/RN-006). */
const NORMALIZE: Partial<Record<FieldKey, (value: string) => string>> = {
  givenName: cleanName,
  familyName: cleanName,
  motherName: cleanName,
  email: normalizeEmail,
}

const GENDER_OPTIONS = [
  { value: 'female', label: locales.genderFemale },
  { value: 'male', label: locales.genderMale },
  { value: 'non_binary', label: locales.genderNonBinary },
  { value: 'prefer_not_to_say', label: locales.genderPreferNotToSay },
]

const EMPTY: Values = {
  cpf: '',
  givenName: '',
  familyName: '',
  email: '',
  dateOfBirth: '',
  gender: '',
  motherName: '',
  password: '',
  confirm: '',
}

const errorText = (code: string | null) =>
  code ? (locales.errors as Record<string, string>)[code] ?? locales.failed : undefined

function validateField(key: FieldKey, values: Values): string | undefined {
  switch (key) {
    case 'cpf':
      return isValidCpf(values.cpf) ? undefined : locales.invalidCpf
    case 'givenName':
      return errorText(validatePersonName(values.givenName, 'givenName'))
    case 'familyName':
      return errorText(validatePersonName(values.familyName, 'familyName'))
    case 'motherName':
      return errorText(validateMotherName(values.motherName))
    case 'email':
      return errorText(validateEmail(values.email))
    case 'dateOfBirth':
      return errorText(validateBirthDate(values.dateOfBirth))
    case 'gender':
      return values.gender.trim() ? undefined : locales.invalidGender
    case 'password':
      return values.password.length < 8 ? locales.weakPassword : undefined
    case 'confirm':
      return values.password !== values.confirm ? locales.passwordMismatch : undefined
  }
}

function normalizeAll(values: Values): Values {
  const next = { ...values }
  for (const [key, normalize] of Object.entries(NORMALIZE)) {
    next[key as FieldKey] = normalize(values[key as FieldKey])
  }
  return next
}

/**
 * Signup single-step (prompt `signup`) — onboarding Auth0.
 * Campos: CPF (username), nome, sobrenome, e-mail, celular, data de
 * nascimento, gênero, senha; nome da mãe (opcional).
 *
 * A tela é uma máquina de etapas: `form → [otp-email] → [otp-sms]`, com cada
 * sub-etapa condicionada aos canais que o BFF exige. A conta só é criada
 * depois de o BFF emitir o token de verificação.
 */
export default function SignupScreen() {
  const {
    startVerification,
    completeSignup,
    handleBackToLogin,
    captchaConfig,
    captchaReady,
    onCaptchaToken,
    error: sdkError,
  } = useSignupManager()

  const [values, setValues] = useState<Values>(EMPTY)
  const [phone, setPhone] = useState('')
  // Erro de cada campo, mostrado nele (inline, aria-invalid).
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<FieldKey, string>>
  >({})

  const verification = useSignupVerification<SignupFormPayload>({
    start: (form, options) => startVerification(form, options),
    complete: (form, token) => completeSignup(form, token),
    describeError,
    failed: locales.failed,
  })
  const { channels, submitted, currentChannel, error, setError, pending } =
    verification

  function describeError(err: unknown): string {
    if (err instanceof Error && err.message === 'CPF_INVALID') {
      return locales.invalidCpf
    }
    if (err instanceof Error && err.message === 'GIVEN_NAME_REQUIRED') {
      return locales.invalidGivenName
    }
    if (err instanceof Error && err.message === 'FAMILY_NAME_REQUIRED') {
      return locales.invalidFamilyName
    }
    if (err instanceof Error && err.message === 'EMAIL_INVALID') {
      return locales.invalidEmail
    }
    if (err instanceof Error && err.message === 'DATE_OF_BIRTH_REQUIRED') {
      return locales.invalidDateOfBirth
    }
    if (err instanceof Error && err.message === 'GENDER_REQUIRED') {
      return locales.invalidGender
    }
    if (err instanceof Error && err.message === 'PHONE_INVALID') {
      return locales.invalidPhone
    }
    if (
      err instanceof Error &&
      (err.message === 'PASSWORD_WEAK' || err.message === 'PASSWORD_POLICY')
    ) {
      return locales.weakPassword
    }
    if (err instanceof Error && err.message === 'CAPTCHA_REQUIRED') {
      return locales.captchaRequired
    }
    // Demais códigos de campo (nome, e-mail) que o manager confere de novo.
    return errorText(err instanceof Error ? err.message : null) ?? locales.failed
  }

  function collectForm(form: Values): SignupFormPayload {
    return {
      username: form.cpf,
      email: form.email,
      phone,
      givenName: form.givenName,
      familyName: form.familyName,
      dateOfBirth: birthDateToIso(form.dateOfBirth),
      gender: form.gender,
      hasMotherName: hasMotherNameFor(form.motherName),
      motherName: hasMotherNameFor(form.motherName) === 'yes' ? form.motherName : '',
      password: form.password,
    }
  }

  function revalidate(key: FieldKey, next: Values) {
    setFieldErrors((prev) => ({
      ...prev,
      [key]: validateField(key, next),
    }))
  }

  /** Campo com erro é revalidado a cada tecla: o erro some assim que o valor fica válido. */
  function change(key: FieldKey, value: string) {
    const next = { ...values, [key]: value }
    setValues(next)
    if (fieldErrors[key]) revalidate(key, next)
  }

  /** Ao sair: normaliza e valida. Campo vazio só acusa no envio, se ainda não acusava. */
  function blur(key: FieldKey) {
    const normalize = NORMALIZE[key]
    const value = normalize ? normalize(values[key]) : values[key]
    const next = { ...values, [key]: value }
    if (value !== values[key]) setValues(next)
    if (value || fieldErrors[key]) revalidate(key, next)
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(undefined)

    const next = normalizeAll(values)
    setValues(next)
    const errors: Partial<Record<FieldKey, string>> = {}
    for (const key of FIELD_KEYS) {
      const message = validateField(key, next)
      if (message) errors[key] = message
    }
    setFieldErrors(errors)
    const first = FIELD_KEYS.find((key) => errors[key])
    if (first) {
      document.getElementById(INPUT_IDS[first])?.focus()
      return
    }

    await verification.begin(collectForm(next))
  }

  if (verification.step === 'otp' && submitted && channels && currentChannel) {
    return (
      <SignupOtpStep
        channel={currentChannel}
        channels={channels}
        destination={
          currentChannel === 'email' ? submitted.email : submitted.phone ?? ''
        }
        currentStep={verification.currentStep}
        totalSteps={verification.totalSteps}
        code={verification.code}
        onCodeChange={verification.setCode}
        error={error || errorCopy(sdkError, locales.errors)}
        pending={pending}
        onSubmit={(event) => void verification.confirm(event)}
        resendSeconds={verification.resendSeconds}
        onResend={() => void verification.resend()}
      />
    )
  }

  return (
    <AleloLayout>
      <AleloStepper currentStep={1} channels={channels ?? undefined} />
      <p className={styles.signupHint}>{`Passo 1 de ${verification.totalSteps}`}</p>
      <h1 className={styles.title}>{locales.title}</h1>
      <p className={styles.subtitle}>{locales.description}</p>
      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        <AleloInput
          id="cpf"
          error={fieldErrors.cpf}
          name="username"
          inputMode="numeric"
          autoComplete="username"
          label={locales.cpfLabel}
          placeholder={locales.cpfPlaceholder}
          value={values.cpf}
          onChange={(e) => change('cpf', formatCpf(e.target.value))}
          maxLength={14}
          required
        />
        <AleloInput
          id="given_name"
          error={fieldErrors.givenName}
          name="given-name"
          autoComplete="given-name"
          autoCapitalize="words"
          spellCheck={false}
          maxLength={NAME_LIMITS.givenName}
          label={locales.givenNameLabel}
          placeholder={locales.givenNamePlaceholder}
          value={values.givenName}
          onChange={(e) => change('givenName', transformInput(e.target, toNameCase))}
          onBlur={() => blur('givenName')}
          required
        />
        <AleloInput
          id="family_name"
          error={fieldErrors.familyName}
          name="family-name"
          autoComplete="family-name"
          autoCapitalize="words"
          spellCheck={false}
          maxLength={NAME_LIMITS.familyName}
          label={locales.familyNameLabel}
          placeholder={locales.familyNamePlaceholder}
          value={values.familyName}
          onChange={(e) => change('familyName', transformInput(e.target, toNameCase))}
          onBlur={() => blur('familyName')}
          required
        />
        <AleloInput
          id="email"
          error={fieldErrors.email}
          name="email"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={EMAIL_MAX_LENGTH}
          label={locales.emailLabel}
          placeholder={locales.emailPlaceholder}
          value={values.email}
          onChange={(e) => change('email', transformInput(e.target, sanitizeEmailInput))}
          onBlur={() => blur('email')}
          required
        />
        <AleloInput
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          label={locales.phoneLabel}
          placeholder={locales.phonePlaceholder}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
        />
        <AleloInput
          id="date_of_birth"
          error={fieldErrors.dateOfBirth}
          name="bday"
          inputMode="numeric"
          autoComplete="bday"
          maxLength={10}
          label={locales.dateOfBirthLabel}
          placeholder={locales.dateOfBirthPlaceholder}
          supportText={locales.dateOfBirthSupport}
          value={values.dateOfBirth}
          onChange={(e) =>
            change('dateOfBirth', transformInput(e.target, formatBirthDateInput))
          }
          onBlur={() => blur('dateOfBirth')}
          required
        />
        <RangoRadioGroup
          id="gender"
          name="sex"
          legend={locales.genderLabel}
          options={GENDER_OPTIONS}
          value={values.gender}
          error={fieldErrors.gender}
          onChange={(value) => change('gender', value)}
        />
        <AleloInput
          id="mother_name"
          error={fieldErrors.motherName}
          name="ulp-mother_name"
          autoCapitalize="words"
          spellCheck={false}
          maxLength={NAME_LIMITS.motherName}
          label={locales.motherNameLabel}
          placeholder={locales.motherNamePlaceholder}
          supportText={locales.motherNameSupport}
          value={values.motherName}
          onChange={(e) => change('motherName', transformInput(e.target, toNameCase))}
          onBlur={() => blur('motherName')}
        />
        <AleloPasswordInput
          id="password"
          error={fieldErrors.password}
          label={locales.passwordLabel}
          name="password"
          autoComplete="new-password"
          placeholder={locales.passwordPlaceholder}
          value={values.password}
          onChange={(e) => change('password', e.target.value)}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
        />
        <AleloPasswordInput
          id="confirm"
          error={fieldErrors.confirm}
          label={locales.confirmPasswordLabel}
          name="password_confirm"
          autoComplete="new-password"
          placeholder={locales.confirmPasswordPlaceholder}
          value={values.confirm}
          onChange={(e) => change('confirm', e.target.value)}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
        />
        <AleloCaptcha config={captchaConfig} onToken={onCaptchaToken} />
        <AleloError message={error || errorCopy(sdkError, locales.errors)} />
        <AleloButton type="submit" disabled={pending || !captchaReady}>
          {locales.submit}
        </AleloButton>
        <div className={styles.center}>
          <AleloButton variant="link" onClick={handleBackToLogin}>
            {locales.backToLogin}
          </AleloButton>
        </div>
      </form>
    </AleloLayout>
  )
}
